import React, { useState } from 'react';

import cx from 'classnames';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import CSSTransition from 'react-transition-group/CSSTransition';

import { PACKAGE_NAMES } from 'Customers/constants';

import InvoiceEditMenu from './InvoiceEditMenu';
import styles from './InvoiceItem.module.scss';
import fadeAnimation from '../../../../components/animations/fadeAnimation.module.scss';
import IconButton from '../../../../components/IconButton';
import OutsideClickHandler from '../../../../components/OutsideClickHandler';
import TableDataCell from '../../../../components/table/TableDataCell';
import formatCurrency from '../../../../utility/formatCurrency';

const InvoiceItem = ({
  id,
  created,
  plan,
  period,
  status,
  amount,
  currency,
  hasFailedPayment,
  failedAttemptsCount,
  lastPaymentAttempt,
  handleDownload,
  actionHandler,
  onRetryPayment,
  hasCardPayment,
  isAdmin,
  isDownloading,
  isDeleting,
  isMarkingPaid,
  selectedId,
  isRetrying,
}) => {
  const [editOpened, setEditOpened] = useState(false);

  const showPaymentButton = isAdmin && status && ['posted', 'payment_due', 'not_paid'].includes(status.toLowerCase());

  const isCurrentItemLoading = selectedId === id && (isDeleting || isMarkingPaid);

  const onDownload = () => {
    handleDownload(id, 'download');
  };
  const onPreview = () => {
    handleDownload(id, 'preview');
  };
  const onDelete = () => {
    actionHandler('delete', id);
  };
  const onMarkAsPaid = () => {
    actionHandler('paid', id);
  };
  return (
    <tr className={cx(styles.row, 't-row border-btm light')}>
      <TableDataCell className='ps-3'>{created ? moment(created).format('MMM DD, YYYY') : '- -'}</TableDataCell>
      <TableDataCell>{PACKAGE_NAMES[plan] || plan}</TableDataCell>
      <TableDataCell>{period}</TableDataCell>
      <TableDataCell>
        <div className='d-flex align-items-center justify-content-center'>
          <div className={cx(styles.status, styles[_.camelCase(status)], 'text-center')}>{_.lowerCase(status)}</div>
          {hasFailedPayment && (
            <span className={styles.failedPaymentIcon}>
              <i className='icon-alert-triangle text-danger ms-2' />
              <span className={styles.tooltip}>
                {(() => {
                  const attempts = failedAttemptsCount || 1;
                  const attemptText = attempts === 1 ? 'Payment failed' : `Payment failed ${attempts} times`;

                  if (lastPaymentAttempt?.comment) {
                    const reason = lastPaymentAttempt.comment
                      .replace(/_/g, ' ')
                      .toLowerCase()
                      .replace(/\b\w/g, (l) => l.toUpperCase());
                    return `${attemptText}\nReason: ${reason}`;
                  }

                  return attemptText;
                })()}
              </span>
            </span>
          )}
        </div>
      </TableDataCell>
      <TableDataCell>{amount ? `${currency} ${formatCurrency(amount)}` : '- -'}</TableDataCell>
      <TableDataCell>
        <div
          className='d-flex justify-content-end'
          onClick={(e) => {
            e.preventDefault();
          }}
        >
          <div className='d-flex'>
            <IconButton
              icon={isDownloading ? 'icon-loading icon-spin me-3' : 'icon-eye me-3'}
              onClick={onPreview}
              disabled={isDownloading || isCurrentItemLoading || isRetrying}
            />
            {status === 'paid' ? (
              <IconButton
                icon={isDownloading ? 'icon-loading icon-spin' : 'icon-download'}
                onClick={onDownload}
                disabled={isDownloading || isCurrentItemLoading || isRetrying}
              />
            ) : (
              <div className='position-relative'>
                <IconButton
                  icon='icon-more-vertical'
                  onClick={() => {
                    setEditOpened(true);
                  }}
                  disabled={isDownloading || isCurrentItemLoading || isRetrying}
                />
                <CSSTransition
                  mountOnEnter
                  unmountOnExit
                  in={editOpened}
                  timeout={200}
                  classNames={{
                    enter: fadeAnimation.enter,
                    enterActive: fadeAnimation.enterActive,
                    exit: fadeAnimation.exit,
                    exitActive: fadeAnimation.exitActive,
                  }}
                >
                  <OutsideClickHandler
                    clickHandler={() => {
                      setEditOpened(false);
                    }}
                  >
                    <InvoiceEditMenu
                      onPaid={() => {
                        onMarkAsPaid();
                        setEditOpened(false);
                      }}
                      onDownload={() => {
                        onDownload();
                        setEditOpened(false);
                      }}
                      onDelete={() => {
                        onDelete();
                        setEditOpened(false);
                      }}
                      onRetryPayment={() => {
                        onRetryPayment(id);
                        setEditOpened(false);
                      }}
                      showPaymentButton={showPaymentButton}
                      hasCardPayment={hasCardPayment}
                      hasFailedPayment={hasFailedPayment}
                      isDownloading={isDownloading}
                      isDeleting={isDeleting}
                      isMarkingPaid={isMarkingPaid}
                      isRetrying={isRetrying}
                      selectedId={selectedId}
                      currentItemId={id}
                    />
                  </OutsideClickHandler>
                </CSSTransition>
              </div>
            )}
          </div>
        </div>
      </TableDataCell>
    </tr>
  );
};

InvoiceItem.propTypes = {
  id: PropTypes.string.isRequired,
  created: PropTypes.string,
  plan: PropTypes.string,
  period: PropTypes.string,
  status: PropTypes.string,
  amount: PropTypes.number,
  currency: PropTypes.string,
  hasFailedPayment: PropTypes.bool,
  failedAttemptsCount: PropTypes.number,
  lastPaymentAttempt: PropTypes.shape({
    status: PropTypes.string,
    time: PropTypes.string,
    comment: PropTypes.string,
  }),
  handleDownload: PropTypes.func.isRequired,
  actionHandler: PropTypes.func.isRequired,
  onRetryPayment: PropTypes.func,
  hasCardPayment: PropTypes.bool,
  isAdmin: PropTypes.bool,
  isDownloading: PropTypes.bool,
  isDeleting: PropTypes.bool,
  isMarkingPaid: PropTypes.bool,
  selectedId: PropTypes.string,
  isRetrying: PropTypes.bool,
};

InvoiceItem.defaultProps = {
  created: null,
  plan: '',
  period: '',
  status: '',
  amount: null,
  currency: '',
  hasFailedPayment: false,
  failedAttemptsCount: 0,
  lastPaymentAttempt: null,
  onRetryPayment: () => {},
  hasCardPayment: false,
  isAdmin: false,
  isDownloading: false,
  isDeleting: false,
  isMarkingPaid: false,
  selectedId: null,
  isRetrying: false,
};

export default InvoiceItem;
